import {useCallback, useEffect, useMemo, useState} from "react";
import {Dialog, DialogBackdrop, DialogPanel, DialogTitle} from "@headlessui/react";
import {notify} from "@/components/Notifications";
import {getErrMsg} from "@/lib/errors";
import {TeamMember, useSaveTeamMember} from "@/hooks/useTeamMembers.ts";

type Props = {
    open: boolean;
    initial?: Partial<TeamMember> | null;
    onClose: () => void;
};

function pickExistingImage(a?: unknown): { id?: string; url?: string } {
    const x = a as any;
    if (!x) return {};
    // Try common fields you might use
    if (x.imageUrl) return {id: x?.id, url: x.imageUrl};
    if (x.avatarUrl) return {id: x?.id, url: x.avatarUrl};
    if (x.photoUrl) return {id: x?.id, url: x.photoUrl};
    return {};
}

export default function TeamMemberUpsertDialog({open, initial, onClose}: Props) {
    const isEdit = Boolean(initial?.id);

    // Core fields
    const [name, setName] = useState(initial?.name ?? "");
    const [description, setDescription] = useState(initial?.description ?? "");

    // File handling
    const [file, setFile] = useState<File | null>(null);
    const [uploadPct, setUploadPct] = useState(0);
    const [touched, setTouched] = useState(false);

    // Existing media / deletion
    const [removedExisting, setRemovedExisting] = useState(false);
    const existing = useMemo(() => pickExistingImage(initial), [initial]);
    const existingImageUrl = removedExisting ? undefined : existing.url;

    // Preview
    const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : undefined), [file]);
    useEffect(() => () => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
    }, [previewUrl]);

    // Upload constraints
    const MAX_MB = 10;
    const ALLOWED_TYPES = useMemo(
        () => new Set(["image/png", "image/jpeg", "image/jpg", "image/gif", "image/webp"]),
        []
    );
    const acceptAttr = useMemo(() => Array.from(ALLOWED_TYPES).join(","), [ALLOWED_TYPES]);

    const {mutateAsync: save, isPending} = useSaveTeamMember();

    useEffect(() => {
        if (open) {
            setName(initial?.name ?? "");
            setDescription(initial?.description ?? "");
            setFile(null);
            setUploadPct(0);
            setRemovedExisting(false);
            setTouched(false);
        }
    }, [open, initial]);

    const onPickFile = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const f = e.target.files?.[0];
            if (!f) return;
            if (!ALLOWED_TYPES.has(f.type)) {
                notify.error(`Invalid file type. Allowed: ${Array.from(ALLOWED_TYPES).join(", ")}`);
                return;
            }
            if (f.size > MAX_MB * 1024 * 1024) {
                notify.error(`File too large. Max size is ${MAX_MB}MB.`);
                return;
            }
            setFile(f);
        },
        [ALLOWED_TYPES]
    );

    const errors = useMemo(() => {
        const e: Record<string, string> = {};
        if (!name.trim()) e.name = "Name is required.";
        if (!description.trim()) e.description = "Description is required.";
        const needsFile = !isEdit || removedExisting;
        if (needsFile && !file) e.file = "Please choose an image file.";
        return e;
    }, [name, description, isEdit, removedExisting, file]);

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setTouched(true);
        if (Object.keys(errors).length) return;

        try {
            await save({
                id: initial?.id,
                name,
                description,
                file,
                onUploadProgress: setUploadPct,
            });
            setUploadPct(0);
            notify.success(`Team member ${isEdit ? "updated" : "created"} successfully.`);
            onClose();
        } catch (err) {
            notify.error(getErrMsg(err, `Failed to ${isEdit ? "update" : "create"} team member. Please try again.`));
        }
    };

    return (
        <Dialog open={open} onClose={onClose} className="relative z-10">
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-gray-800/75 data-[closed]:opacity-0 data-[enter]:duration-300 data-[enter]:ease-out data-[leave]:duration-200 data-[leave]:ease-in"
            />
            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                    <DialogPanel
                        transition
                        className="relative w-full max-w-md sm:max-w-lg md:max-w-xl transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[enter]:ease-out data-[leave]:duration-200 data-[leave]:ease-in sm:my-8 sm:p-6 sm:data-[closed]:translate-y-0 sm:data-[closed]:scale-95"
                    >
                        <DialogTitle as="h3" className="text-lg font-semibold text-blue-900">
                            {isEdit ? "Edit Team Member" : "Add Team Member"}
                        </DialogTitle>

                        <form onSubmit={onSubmit} noValidate>
                            <div className="mt-6 grid grid-cols-1 gap-6">
                                {/* Name */}
                                <div>
                                    <label htmlFor="name" className="block text-sm font-medium text-blue-900">
                                        Name
                                    </label>
                                    <input
                                        id="name"
                                        type="text"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        required
                                        aria-invalid={touched && !!errors.name}
                                        className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base text-blue-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800 sm:text-sm/6"
                                    />
                                    {touched && errors.name &&
                                        <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
                                </div>

                                {/* Description */}
                                <div>
                                    <label htmlFor="description" className="block text-sm font-medium text-blue-900">
                                        Description
                                    </label>
                                    <textarea
                                        id="description"
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        rows={5}
                                        required
                                        aria-invalid={touched && !!errors.description}
                                        className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base text-blue-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800 sm:text-sm/6"
                                    />
                                    {touched && errors.description && (
                                        <p className="mt-1 text-xs text-red-600">{errors.description}</p>
                                    )}
                                </div>

                                {/* Image */}
                                <div>
                                    <div className="flex items-center justify-between">
                                        <label htmlFor="file" className="block text-sm font-medium text-blue-900">
                                            Photo
                                        </label>
                                        {isEdit && existingImageUrl && (
                                            <button
                                                type="button"
                                                onClick={() => setRemovedExisting((v) => !v)}
                                                className="text-xs text-gray-600 hover:text-blue-900"
                                            >
                                                {removedExisting ? "Keep existing photo" : "Remove existing photo"}
                                            </button>
                                        )}
                                    </div>

                                    {(previewUrl || existingImageUrl) && (
                                        <div className="mt-2">
                                            <img
                                                src={previewUrl || existingImageUrl}
                                                alt="Team member photo"
                                                className="h-full w-full rounded-md object-cover ring-1 ring-gray-200"
                                            />
                                        </div>
                                    )}

                                    <div className="mt-3 flex flex-wrap items-center gap-3">
                                        <input
                                            id="file"
                                            name="file-upload"
                                            type="file"
                                            accept={acceptAttr}
                                            onChange={onPickFile}
                                            className="block w-full sm:w-auto text-sm text-gray-700 file:mr-4 file:rounded-md file:border-0 file:bg-indigo-50 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-indigo-700 hover:file:bg-indigo-100"
                                        />
                                        {file && (
                                            <button
                                                type="button"
                                                onClick={() => setFile(null)}
                                                className="text-sm text-gray-600 hover:text-blue-900"
                                            >
                                                Clear selection
                                            </button>
                                        )}
                                    </div>

                                    {touched && errors.file &&
                                        <p className="mt-1 text-xs text-red-600">{errors.file}</p>}

                                    {uploadPct > 0 && uploadPct < 100 && (
                                        <div className="mt-3 h-2 w-full overflow-hidden rounded bg-gray-100">
                                            <div className="h-full bg-blue-900 transition-all"
                                                 style={{width: `${uploadPct}%`}}/>
                                        </div>
                                    )}
                                    <p className="mt-1 text-xs text-gray-500">PNG, JPG, GIF, WEBP up to {MAX_MB}MB.</p>
                                </div>

                                {/* Actions */}
                                <div className="mt-2 flex items-center justify-end gap-x-3">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        disabled={isPending}
                                        className="text-sm font-semibold text-blue-900"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isPending}
                                        className="inline-flex items-center rounded-md bg-blue-900 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-blue-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800 disabled:opacity-60"
                                    >
                                        {isPending ? (isEdit ? "Saving…" : "Creating…") : isEdit ? "Save" : "Create"}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
}
